// Pure derivation for the Inicio operator deck. Turns the org's active
// diligencias (real /tasks rows) into the view-model
// with METRICS / DILIGENCIAS / PLAZOS: the Foco cards, the KPI numbers,
// and the 5-business-day agenda. Every number is derived here — never a literal.
//
// The estado business rules live in lib/diligenciaDisplay (single source of
// truth, shared with Causas/Diligencias via causaDiligenciaStage). We call it
// with an explicit `now` (causaDiligenciaStage hardcodes Date.now(), which the
// dashboard can't use — it anchors `now` at mount) and adapt its output: the
// bucket map is stageKeyForStage and the attn formula below is the same glue
// causaDiligenciaStage applies, not a re-derivation of the rules. No React,
// no navigation.
import { diligenciaDisplay } from '@/lib/diligenciaDisplay';
import { STAGE_TONE, stageKeyForStage } from '@/lib/diligenciaStage';
import { titleCase } from '@/lib/text';
import {
  MS_PER_DAY,
  addCalendarDay,
  chileYmd,
  deadlineCalendarDays,
  isDeadlineExpired,
} from '@/lib/dateUtils';

const DOW = ['DOM', 'LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB'];

// Foco ordering (focusRank): most severe first, then soonest deadline.
const TONE_RANK = {
  err: 0,
  ready: 1,
  warn: 2,
  live: 3,
  info: 4,
  mute: 5,
  ok: 6,
};

function dayKey(date) {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function daysUntil(deadlineIso, now, expiresAt) {
  if (!deadlineIso) return Infinity;
  const t = new Date(deadlineIso).getTime();
  if (Number.isNaN(t)) return Infinity;
  return (
    deadlineCalendarDays(deadlineIso, now, expiresAt) ??
    Math.round((t - now) / MS_PER_DAY)
  );
}

// Enrich each active (non-closed) diligencia with its derived estado, tone and
// attention flag. `tasks` are raw /tasks rows (getTasks → extractList).
export function enrichDiligencias(tasks, now = Date.now()) {
  return (tasks || [])
    .map((t) => {
      const disp = diligenciaDisplay(t.stage, t.deadline, now, {
        deadlineExpiresAt: t.deadlineExpiresAt,
        dateOffered: t.dateOffered,
      });
      // Bucket: the served stage through the shared map. CANCELLED and CLOSED
      // land in 'cerrada', so a cancelled diligencia counts in no metric,
      // foco nor agenda.
      let stage = stageKeyForStage(t.stage);
      // An expired pre-commitment diligencia (any pre-IN_PROGRESS stage past
      // its plazo) is terminal: it belongs in Historial, not the active deck.
      // Same for an IN_PROGRESS past its deadline: cierre natural — the plazo
      // fulfilled concludes the process, so it reads Finalizada and drops out
      // of foco/agenda/active metrics like any closed diligencia.
      if (disp.expired || disp.label === 'Finalizada') {
        stage = 'cerrada';
      }
      // attn: needs operator action now (same formula as causaDiligenciaStage).
      const attn =
        disp.row === 'err' || disp.tone === 'warn' || stage === 'porpagar';
      return {
        taskId: t.id ?? null,
        caseId: t.caseId ?? null,
        caseRol: t.caseRol ?? null,
        caratula: t.caseTitle ? titleCase(t.caseTitle) : t.caseRol || '—',
        deadline: t.deadline ?? null,
        deadlineExpiresAt: t.deadlineExpiresAt ?? null,
        days: daysUntil(t.deadline, now, t.deadlineExpiresAt),
        stage,
        attn,
        // Foco tone escalates (err/warn) via the rich display model; falls back
        // to the plain stage tone otherwise.
        tone: disp.tone || STAGE_TONE[stage] || 'info',
        label: disp.label,
        alert: disp.alert || disp.sub || '',
      };
    })
    .filter((d) => d.stage && d.stage !== 'cerrada');
}

function ctaFor(d) {
  if (d.stage === 'porpagar') return d.tone === 'err' ? 'Reintentar' : 'Pagar';
  if (d.stage === 'borrador') return 'Completar';
  if (d.tone === 'err' || d.tone === 'warn') return 'Revisar';
  return 'Ver diligencia';
}

// Route the Foco card opens: the diligencia detail whenever there's a task id
// — paying included, the detail is where a single diligencia gets paid
// (REC-1117). Without one, porpagar falls back to the pay queue, else the
// causa.
function focusRoute(d) {
  if (d.taskId) return `/diligencias/${d.taskId}`;
  if (d.stage === 'porpagar') return '/pagos';
  return d.caseId ? `/causas/${d.caseId}` : '/diligencias';
}

// Top-3 diligencias needing attention, ranked by tone then deadline. Items carry
// a `route`; the page turns it into an onClick.
export function buildFocus(enriched) {
  return [...enriched]
    .filter((d) => d.attn)
    .sort(
      (a, b) =>
        (TONE_RANK[a.tone] ?? 9) - (TONE_RANK[b.tone] ?? 9) || a.days - b.days,
    )
    .slice(0, 3)
    .map((d) => ({
      key: d.taskId || d.caseId || d.caseRol,
      tone: d.tone,
      tagLabel: d.label,
      idLabel: d.caseRol,
      what: d.alert,
      caratula: d.caratula,
      ctaLabel: ctaFor(d),
      route: focusRoute(d),
    }));
}

export function buildMetrics(enriched, { causasActivas } = {}) {
  const distinctCauses = new Set(enriched.map((d) => d.caseId).filter(Boolean));
  return {
    diligenciasActivas: enriched.length,
    requierenAccion: enriched.filter((d) => d.attn).length,
    cotizando: enriched.filter((d) => d.stage === 'cotizando').length,
    listo: enriched.filter((d) => d.label === 'Listo para pagar').length,
    enCurso: enriched.filter((d) => d.stage === 'encurso').length,
    causasActivas: causasActivas ?? distinctCauses.size,
  };
}

const KIND = { err: 'err', warn: 'warn' };

// The next `count` business days (Mon–Fri) from `now`, each bucketing the
// diligencias whose deadline falls on that day. Weekends are skipped entirely.
export function buildAgenda(enriched, now = Date.now(), count = 5) {
  const byDay = new Map();
  for (const d of enriched) {
    if (!d.deadline) continue;
    const dt = new Date(d.deadline);
    if (Number.isNaN(dt.getTime())) continue;
    // La columna de la agenda representa el día elegido, no un instante local.
    const k = `${dt.getUTCFullYear()}-${dt.getUTCMonth()}-${dt.getUTCDate()}`;
    if (!byDay.has(k)) byDay.set(k, []);
    byDay.get(k).push(d);
  }

  const days = [];
  const calendar = enriched.some(
    (d) => isDeadlineExpired(d.deadline, d.deadlineExpiresAt, now) !== null,
  );
  const today = calendar ? chileYmd(new Date(now)) : null;
  const cursor = calendar ? new Date(`${today}T00:00:00Z`) : new Date(now);
  if (!calendar) cursor.setHours(0, 0, 0, 0);
  const key = (date) =>
    calendar
      ? `${date.getUTCFullYear()}-${date.getUTCMonth()}-${date.getUTCDate()}`
      : dayKey(date);
  const todayKey = key(cursor);
  let guard = 0;
  while (days.length < count && guard < 21) {
    guard += 1;
    const dow = calendar ? cursor.getUTCDay() : cursor.getDay();
    if (dow !== 0 && dow !== 6) {
      const k = key(cursor);
      const items = (byDay.get(k) || []).map((d) => ({
        key: d.taskId || d.caseId || d.caseRol,
        kind: KIND[d.tone] || 'info',
        what: d.label,
        secondary: d.caseRol || d.caratula,
        route: d.taskId
          ? `/diligencias/${d.taskId}`
          : d.caseId
            ? `/causas/${d.caseId}`
            : '/diligencias',
      }));
      days.push({
        key: k,
        dow: DOW[dow],
        dd: String(calendar ? cursor.getUTCDate() : cursor.getDate()).padStart(
          2,
          '0',
        ),
        hot: k === todayKey,
        items,
      });
    }
    if (calendar)
      cursor.setTime(
        Date.parse(addCalendarDay(cursor.toISOString().slice(0, 10), 1)),
      );
    else cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}
